/**
 * Utilities for working with structured document content.
 * These functions extract plain text from the rich AST structure
 * for use in previews, search indexing, and other text-based operations.
 */
import { ContentBlock, InlineElement, StructuredDoc } from './api';

/**
 * Extract plain text from inline elements.
 * Recursively processes nested inline elements (links, emphasis, etc.)
 */
const extractTextFromInlineElements = (elements: InlineElement[]): string => {
  return elements
    .map((element) => {
      switch (element.type) {
        case 'text':
          return element.content;
        case 'concept_link':
        case 'link':
          return extractTextFromInlineElements(element.text);
        case 'emphasis':
        case 'strong':
          return extractTextFromInlineElements(element.content);
        case 'code':
          return element.content;
        case 'soft_break':
          return ' ';
        case 'line_break':
          return ' ';
        case 'image':
          return element.alt || '';
        default:
          return '';
      }
    })
    .join('');
};

/**
 * Extract plain text from a content block.
 * Handles all block types and recursively processes nested structures.
 */
const extractTextFromBlock = (block: ContentBlock): string => {
  switch (block.type) {
    case 'paragraph':
    case 'heading':
      return extractTextFromInlineElements(block.inline_elements);
    case 'list':
      return block.items.map((item) => item.blocks.map(extractTextFromBlock).join(' ')).join(' ');
    case 'block_quote':
      return block.blocks.map(extractTextFromBlock).join(' ');
    case 'code_block':
      return block.code;
    case 'offer_comparison_table':
      return block.rows.map((row) => `${row.offer_name}: ${row.comments}`).join(' ');
    case 'table':
      return block.rows
        .map((row) =>
          row.cells.map((cell) => extractTextFromInlineElements(cell.inline_elements)).join(' ')
        )
        .join(' ');
    case 'thematic_break':
      return '';
    default:
      return '';
  }
};

/**
 * Extract all plain text from a structured document.
 * Useful for search indexing, preview generation, etc.
 */
export const extractPlainText = (structuredDoc: StructuredDoc): string => {
  return structuredDoc.blocks.map(extractTextFromBlock).join(' ');
};
